import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../Config';
import {
  format,
  parseISO,
  isValid,
  formatDistanceToNow
} from 'date-fns';
import { fr } from 'date-fns/locale';

// Icônes
import {
  FiPlus,
  FiTrash2,
  FiEye,
  FiEdit2,
  FiPackage,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiSearch,
  FiChevronRight,
  FiChevronDown,
  FiCalendar,
  FiPlusCircle,
  FiMinusCircle,
  FiCheckCircle,
  FiXCircle
} from 'react-icons/fi';
import {
  HiOutlineDocumentText,
  HiOutlineExclamationCircle,
  HiOutlineInformationCircle,
  HiOutlineCollection
} from 'react-icons/hi';
import { RiGraduationCapLine } from 'react-icons/ri';

const PacksEnterrementPage = () => {
  const [packs, setPacks] = useState([]);
  const [materiels, setMateriels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [materielsLoading, setMaterielsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    materiels: []
  });
  const [newMateriel, setNewMateriel] = useState({ id: '', quantite: 1 });
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const api = axios.create({
    baseURL: API_URL,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  useEffect(() => {
    fetchPacks();
    fetchMateriels();
  }, []);

  const fetchPacks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/packs-enterrement');
      setPacks(response.data);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des packs', 'error');
      console.error('Error fetching packs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMateriels = async () => {
    try {
      setMaterielsLoading(true);
      const response = await api.get('/materiels');
      
      let materielsData = response.data;
      
      // Gestion de différentes structures de réponse
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        materielsData = response.data.data;
      } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const arrayKeys = Object.keys(response.data).filter(key => Array.isArray(response.data[key]));
        if (arrayKeys.length > 0) {
          materielsData = response.data[arrayKeys[0]];
        }
      }
      
      if (Array.isArray(materielsData)) {
        setMateriels(materielsData);
      } else {
        console.error('Les matériels ne sont pas un tableau:', materielsData);
        setMateriels([]);
        showSnackbar('Format de données incorrect pour les matériels', 'warning');
      }
      
    } catch (error) {
      console.error('Error fetching materiels:', error);
      showSnackbar('Erreur lors du chargement des matériels', 'error');
      setMateriels([]);
    } finally {
      setMaterielsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMaterielChange = (e) => {
    const { name, value } = e.target;
    setNewMateriel(prev => ({
      ...prev,
      [name]: name === 'quantite' ? parseInt(value) || 1 : value
    }));
  };

  const addMateriel = () => {
    if (!newMateriel.id || newMateriel.quantite < 1) {
      showSnackbar('Veuillez sélectionner un matériel et spécifier une quantité valide', 'warning');
      return;
    }

    const materiel = materiels.find(m => m.id == newMateriel.id);
    if (!materiel) {
      showSnackbar('Matériel non trouvé', 'error');
      return;
    }

    const exists = formData.materiels.find(m => m.id == newMateriel.id);
    if (exists) {
      showSnackbar('Ce matériel est déjà dans la liste', 'warning');
      return;
    }

    setFormData(prev => ({
      ...prev,
      materiels: [...prev.materiels, {
        id: newMateriel.id,
        quantite: newMateriel.quantite,
        nom: materiel.nom,
        description: materiel.description || ''
      }]
    }));

    setNewMateriel({ id: '', quantite: 1 });
  };

  const removeMateriel = (id) => {
    setFormData(prev => ({
      ...prev,
      materiels: prev.materiels.filter(m => m.id != id)
    }));
  };

  const updateMaterielQuantite = (id, change) => {
    setFormData(prev => ({
      ...prev,
      materiels: prev.materiels.map(m => {
        if (m.id == id) {
          const newQuantite = m.quantite + change;
          if (newQuantite < 1) {
            return null;
          }
          return { ...m, quantite: newQuantite };
        }
        return m;
      }).filter(m => m !== null)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (formData.materiels.length === 0) {
      newErrors.materiels = 'Au moins un matériel est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      const response = await api.post('/packs-enterrement', {
        nom: formData.nom,
        description: formData.description,
        materiels: formData.materiels.map(m => ({
          id: m.id,
          quantite: m.quantite
        }))
      });
      setPacks(prev => [...prev, response.data]);
      showSnackbar('Pack créé avec succès', 'success');
      resetForm();
      setOpenDialog(false);
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la création';
      showSnackbar(message, 'error');
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;

    try {
      const response = await api.put(`/packs-enterrement/${selectedPack.id}`, {
        nom: formData.nom,
        description: formData.description,
        materiels: formData.materiels.map(m => ({
          id: m.id,
          quantite: m.quantite
        }))
      });
      setPacks(prev => 
        prev.map(p => p.id === selectedPack.id ? response.data : p)
      );
      showSnackbar('Pack modifié avec succès', 'success');
      resetForm();
      setOpenDialog(false);
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la modification';
      showSnackbar(message, 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/packs-enterrement/${selectedPack.id}`);
      setPacks(prev => prev.filter(p => p.id !== selectedPack.id));
      showSnackbar('Pack supprimé avec succès', 'success');
      setOpenDeleteDialog(false);
      setSelectedPack(null);
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la suppression';
      showSnackbar(message, 'error');
    }
  };

  const handleEdit = (pack) => {
    setSelectedPack(pack);
    setFormData({
      nom: pack.nom,
      description: pack.description || '',
      materiels: pack.materiels ? pack.materiels.map(m => ({
        id: m.id,
        quantite: m.pivot?.quantite || 1,
        nom: m.nom,
        description: m.description || ''
      })) : []
    });
    setOpenDialog(true);
  };

  const handleViewDetails = (pack) => {
    setSelectedPack(pack);
    setOpenDetailsDialog(true);
  };

  const handleDeleteClick = (pack) => {
    setSelectedPack(pack);
    setOpenDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      materiels: []
    });
    setNewMateriel({ id: '', quantite: 1 });
    setErrors({});
    setSelectedPack(null);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getMaterielCount = (pack) => {
    return pack.materiels ? pack.materiels.length : 0;
  };

  const getTotalItems = (pack) => {
    if (!pack.materiels) return 0;
    return pack.materiels.reduce((total, materiel) => 
      total + (materiel.pivot?.quantite || 1), 0
    );
  };

  const filteredAndSortedPacks = useMemo(() => {
    let filtered = packs.filter(pack => {
      const matchesSearch = searchTerm === '' || 
        pack.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pack.description && pack.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        pack.id.toString().includes(searchTerm);
      
      return matchesSearch;
    });

    filtered.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [packs, searchTerm, sortConfig]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return dateString;
      return format(date, 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return '';
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des packs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="bg-blue-50 p-2 rounded-lg mr-3">
                <RiGraduationCapLine className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Packs d'Enterrement</h1>
                <p className="text-sm text-gray-500">Gérez vos packs pré-configurés</p>
              </div>
              <span className="ml-4 bg-gray-100 text-gray-600 text-sm font-medium px-2.5 py-0.5 rounded-full">
                {packs.length} pack(s)
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={fetchPacks}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <FiRefreshCw className="w-4 h-4" />
                Actualiser
              </button>
              <button
                onClick={() => {
                  resetForm();
                  setOpenDialog(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <FiPlus className="w-4 h-4" />
                Nouveau Pack
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total des Packs</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{packs.length}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <HiOutlineCollection className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Matériels uniques</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">
                  {[...new Set(packs.flatMap(p => p.materiels?.map(m => m.id) || []))].length}
                </p>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg">
                <FiPackage className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Dernière création</p>
                <p className="text-lg font-semibold text-gray-900 mt-2">
                  {packs.length > 0 ? formatDate(packs[packs.length - 1].created_at) : 'N/A'}
                </p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <FiCalendar className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Items totaux</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">
                  {packs.reduce((total, pack) => total + getTotalItems(pack), 0)}
                </p>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <FiPackage className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, description ou ID..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <FiFilter className="text-gray-400" />
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={sortConfig.key}
                  onChange={(e) => setSortConfig(prev => ({ 
                    ...prev, 
                    key: e.target.value 
                  }))}
                >
                  <option value="created_at">Trier par date</option>
                  <option value="nom">Trier par nom</option>
                  <option value="id">Trier par ID</option>
                </select>
                <button
                  onClick={() => setSortConfig(prev => ({ 
                    ...prev, 
                    direction: prev.direction === 'asc' ? 'desc' : 'asc' 
                  }))}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                </button>
              </div>
              
              <button
                onClick={() => {}}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:"
              >
                <FiDownload className="w-4 h-4" />
                <span className="text-sm font-medium">Exporter</span>
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        {filteredAndSortedPacks.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <RiGraduationCapLine className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun pack trouvé
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm 
                ? 'Aucun pack ne correspond à votre recherche'
                : 'Commencez par créer votre premier pack d\'enterrement'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  resetForm();
                  setOpenDialog(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <FiPlus className="w-4 h-4" />
                Créer un pack
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pack
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Matériels
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date de création
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAndSortedPacks.map((pack) => {
                    const isExpanded = expandedRows[pack.id];
                    const materielCount = getMaterielCount(pack);
                    const totalItems = getTotalItems(pack);
                    
                    return (
                      <React.Fragment key={pack.id}>
                        <tr className="hover:">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="bg-blue-50 p-2 rounded-lg">
                                <RiGraduationCapLine className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  #{pack.id} • {pack.nom}
                                </div>
                                <div className="text-xs text-gray-500">
                                  ID: {pack.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <p className="text-sm text-gray-600 truncate">
                                {pack.description || 'Aucune description'}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center space-x-2">
                                <div className="bg-emerald-100 text-emerald-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                  {materielCount} mat.
                                </div>
                                <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                  {totalItems} unités
                                </div>
                              </div>
                              <button
                                onClick={() => toggleRowExpansion(pack.id)}
                                className="p-1 text-gray-400 hover:text-gray-600"
                              >
                                {isExpanded ? (
                                  <FiChevronDown className="w-4 h-4" />
                                ) : (
                                  <FiChevronRight className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {formatDate(pack.created_at)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {getTimeAgo(pack.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleViewDetails(pack)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Voir les détails"
                              >
                                <FiEye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEdit(pack)}
                                className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                title="Modifier"
                              >
                                <FiEdit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(pack)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Supprimer"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Row Expansion */}
                        {isExpanded && pack.materiels && pack.materiels.length > 0 && (
                          <tr className="">
                            <td colSpan="5" className="px-6 py-4">
                              <div className="pl-14">
                                <div className="bg-white rounded-lg border border-gray-200 p-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-3">Matériels inclus</h4>
                                  <div className="space-y-2">
                                    {pack.materiels.slice(0, 5).map((materiel, index) => (
                                      <div key={materiel.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                        <div className="flex items-center space-x-3">
                                          <div className="bg-gray-100 p-2 rounded-lg">
                                            <FiPackage className="w-4 h-4 text-gray-600" />
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium text-gray-900">{materiel.nom}</p>
                                            {materiel.description && (
                                              <p className="text-xs text-gray-500">{materiel.description}</p>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                          <span className="text-sm font-medium text-gray-900">
                                            {materiel.pivot?.quantite || 1} unité(s)
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                    {pack.materiels.length > 5 && (
                                      <div className="text-center pt-2">
                                        <span className="text-xs text-gray-500">
                                          + {pack.materiels.length - 5} autre(s) matériel(s)
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Dialog Création/Modification */}
      {openDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity 0 bg-opacity-75" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <RiGraduationCapLine className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedPack ? 'Modifier le pack' : 'Nouveau pack d\'enterrement'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {selectedPack ? `ID: ${selectedPack.id}` : 'Configurez un nouveau pack'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setOpenDialog(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-b border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Formulaire principal */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom du pack <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="nom"
                        value={formData.nom}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                          errors.nom ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Ex: Pack funéraire standard"
                      />
                      {errors.nom && (
                        <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        rows="4"
                        placeholder="Description détaillée du pack..."
                      />
                    </div>
                  </div>
                  
                  {/* Matériels */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Ajouter des matériels <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                          Sélectionnez les matériels à inclure dans le pack
                        </p>
                      </div>
                      <span className="text-sm font-medium text-gray-600">
                        {formData.materiels.length} matériel(s)
                      </span>
                    </div>
                    
                    {errors.materiels && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-600 flex items-center">
                          <FiAlertCircle className="w-4 h-4 mr-2" />
                          {errors.materiels}
                        </p>
                      </div>
                    )}
                    
                    {/* Formulaire d'ajout */}
                    <div className=" rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Matériel
                          </label>
                          <select
                            name="id"
                            value={newMateriel.id}
                            onChange={handleMaterielChange}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            disabled={materielsLoading}
                          >
                            <option value="">Sélectionner un matériel</option>
                            {materiels.map((materiel) => (
                              <option key={materiel.id} value={materiel.id}>
                                {materiel.nom}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Quantité
                          </label>
                          <input
                            type="number"
                            name="quantite"
                            value={newMateriel.quantite}
                            onChange={handleMaterielChange}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            min="1"
                          />
                        </div>
                      </div>
                      
                      <button
                        onClick={addMateriel}
                        className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <FiPlusCircle className="w-4 h-4" />
                        Ajouter au pack
                      </button>
                    </div>
                    
                    {/* Liste des matériels */}
                    {formData.materiels.length > 0 && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className=" px-4 py-3 border-b border-gray-200">
                          <h4 className="text-sm font-medium text-gray-700">Matériels sélectionnés</h4>
                        </div>
                        <div className="divide-y divide-gray-200 max-h-60 overflow-y-auto">
                          {formData.materiels.map((materiel) => (
                            <div key={materiel.id} className="px-4 py-3 hover:">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="bg-emerald-50 p-2 rounded-lg">
                                    <FiPackage className="w-4 h-4 text-emerald-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{materiel.nom}</p>
                                    {materiel.description && (
                                      <p className="text-xs text-gray-500">{materiel.description}</p>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="flex items-center space-x-4">
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => updateMaterielQuantite(materiel.id, -1)}
                                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                    >
                                      <FiMinusCircle className="w-4 h-4" />
                                    </button>
                                    <span className="text-sm font-medium text-gray-900 min-w-8 text-center">
                                      {materiel.quantite}
                                    </span>
                                    <button
                                      onClick={() => updateMaterielQuantite(materiel.id, 1)}
                                      className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                                    >
                                      <FiPlusCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                  
                                  <button
                                    onClick={() => removeMateriel(materiel.id)}
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  >
                                    <FiTrash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4  flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setOpenDialog(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover: transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={selectedPack ? handleUpdate : handleCreate}
                  className="px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <FiCheck className="w-4 h-4" />
                  {selectedPack ? 'Modifier' : 'Créer le pack'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog Détails */}
      {openDetailsDialog && selectedPack && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity 0 bg-opacity-75" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <RiGraduationCapLine className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedPack.nom}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Pack #{selectedPack.id} • Créé {getTimeAgo(selectedPack.created_at)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpenDetailsDialog(false)}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-b border-gray-200">
                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                    <div className=" rounded-lg p-4">
                      <p className="text-sm text-gray-600">
                        {selectedPack.description || 'Aucune description disponible'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Matériels */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Matériels inclus</h4>
                      <span className="text-sm text-gray-500">
                        {getMaterielCount(selectedPack)} matériel(s) • {getTotalItems(selectedPack)} unité(s)
                      </span>
                    </div>
                    
                    {selectedPack.materiels && selectedPack.materiels.length > 0 ? (
                      <div className="space-y-3">
                        {selectedPack.materiels.map((materiel) => (
                          <div key={materiel.id} className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-3">
                                <div className="bg-emerald-50 p-2 rounded-lg">
                                  <FiPackage className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{materiel.nom}</p>
                                  {materiel.description && (
                                    <p className="text-sm text-gray-500 mt-1">{materiel.description}</p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="text-lg font-semibold text-blue-600">
                                  {materiel.pivot?.quantite || 1}
                                </span>
                                <p className="text-xs text-gray-500">unité(s)</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className=" rounded-lg p-8 text-center">
                        <FiPackage className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">Aucun matériel dans ce pack</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Informations */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Date de création</h4>
                      <p className="text-sm text-gray-900">{formatDate(selectedPack.created_at)}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Dernière mise à jour</h4>
                      <p className="text-sm text-gray-900">
                        {selectedPack.updated_at ? formatDate(selectedPack.updated_at) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4  flex justify-end space-x-3">
                <button
                  onClick={() => handleEdit(selectedPack)}
                  className="px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <FiEdit2 className="w-4 h-4" />
                  Modifier
                </button>
                <button
                  onClick={() => setOpenDetailsDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover: transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog Suppression */}
      {openDeleteDialog && selectedPack && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity 0 bg-opacity-75" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Confirmer la suppression</h3>
                  <button
                    onClick={() => setOpenDeleteDialog(false)}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-b border-gray-200">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-100 p-3 rounded-full flex-shrink-0">
                    <HiOutlineExclamationCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-gray-700">
                      Êtes-vous sûr de vouloir supprimer le pack{' '}
                      <span className="font-semibold">"{selectedPack.nom}"</span> ?
                    </p>
                    <div className="mt-3 p-3  rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="bg-blue-50 p-2 rounded-lg">
                          <RiGraduationCapLine className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{selectedPack.nom}</p>
                          <p className="text-xs text-gray-500">
                            {getMaterielCount(selectedPack)} matériel(s) • {getTotalItems(selectedPack)} unité(s)
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-red-600 mt-3">
                      ⚠️ Cette action est irréversible et supprimera toutes les associations.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4  flex justify-end space-x-3">
                <button
                  onClick={() => setOpenDeleteDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover: transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 rounded-lg text-sm font-medium text-white hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <FiTrash2 className="w-4 h-4" />
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className={`rounded-lg shadow-lg p-4 max-w-md transform transition-all duration-300 ${
            snackbar.severity === 'success' 
              ? 'bg-emerald-50 border border-emerald-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start">
              <div className={`flex-shrink-0 ${
                snackbar.severity === 'success' ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {snackbar.severity === 'success' ? (
                  <FiCheckCircle className="w-5 h-5" />
                ) : (
                  <HiOutlineExclamationCircle className="w-5 h-5" />
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className={`text-sm font-medium ${
                  snackbar.severity === 'success' ? 'text-emerald-800' : 'text-red-800'
                }`}>
                  {snackbar.message}
                </p>
              </div>
              <button
                onClick={() => setSnackbar(prev => ({ ...prev, open: false }))}
                className="ml-4 flex-shrink-0"
              >
                <FiX className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PacksEnterrementPage;